import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#0A1628] text-white">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/">
            <img
              src="https://static.readdy.ai/image/d6b4b88b420755e3ba36fdd8ab8e8672/43b1a7a9dc913fdbd622cad5f4bf97fa.png"
              alt="Tri County Ninja"
              className="h-12 w-auto"
            />
          </Link>
          <Link
            to="/"
            className="flex items-center text-white/80 hover:text-[#FF6B35] transition-colors duration-300 text-sm font-medium cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-[#0A1628] text-white pb-16 pt-10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF6B35]/20 rounded-full mb-6">
            <i className="ri-shield-check-line text-[#FF6B35] text-3xl"></i>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-white/60 text-sm">Last updated: April 15, 2026</p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="prose max-w-none">

          <section className="mb-12">
            <p className="text-gray-600 text-base leading-relaxed">
              Tri County Ninja ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services at our facility located at 14283 Fenton Rd, Fenton, MI 48430. Please read this policy carefully. If you disagree with its terms, please discontinue use of our site and services.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">1</span>
              Information We Collect
            </h2>
            <div className="pl-11">
              <h3 className="text-lg font-semibold text-[#0A1628] mb-2">Personal Information</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                We may collect personally identifiable information that you voluntarily provide when registering for classes, purchasing memberships, or contacting us, including:
              </p>
              <ul className="list-none space-y-2 mb-6">
                {[
                  'Full name and date of birth',
                  'Email address and phone number',
                  'Mailing address',
                  'Payment and billing information',
                  'Emergency contact details',
                  'Health or medical information relevant to participation',
                  'Photos or videos taken during events or classes (with consent)',
                ].map((item) => (
                  <li key={item} className="flex items-start text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#FF6B35] mr-3 mt-0.5 shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
              <h3 className="text-lg font-semibold text-[#0A1628] mb-2">Automatically Collected Information</h3>
              <p className="text-gray-600 leading-relaxed">
                When you visit our website, we may automatically collect certain information about your device, including your IP address, browser type, operating system, referring URLs, and pages viewed. This data helps us improve our website and understand how visitors use it.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">2</span>
              How We Use Your Information
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 mb-4 leading-relaxed">We use the information we collect to:</p>
              <ul className="list-none space-y-2">
                {[
                  'Process registrations, memberships, and payments',
                  'Communicate with you about classes, events, and schedule changes',
                  'Send promotional emails and newsletters (you may opt out at any time)',
                  'Ensure the safety of all participants at our facility',
                  'Comply with legal obligations and enforce our policies',
                  'Improve our website, programs, and overall customer experience',
                  'Respond to inquiries and provide customer support',
                ].map((item) => (
                  <li key={item} className="flex items-start text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#FF6B35] mr-3 mt-0.5 shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">3</span>
              Sharing Your Information
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 mb-4 leading-relaxed">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-none space-y-2">
                {[
                  'With trusted service providers who assist in operating our website and business (e.g., payment processors), under strict confidentiality agreements',
                  'When required by law, court order, or governmental authority',
                  'To protect the rights, property, or safety of Tri County Ninja, our members, or others',
                  'In connection with a merger, acquisition, or sale of all or a portion of our assets',
                ].map((item) => (
                  <li key={item} className="flex items-start text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#FF6B35] mr-3 mt-0.5 shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">4</span>
              Cookies &amp; Tracking Technologies
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                Our website may use cookies and similar tracking technologies to enhance your browsing experience, analyze site traffic, and personalize content. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, some features of our website may not function properly without cookies.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">5</span>
              Children&apos;s Privacy
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                Our services are designed for children and families. We collect information about minors only with verifiable parental or guardian consent. Parents and guardians may review, update, or request deletion of their child&apos;s personal information by contacting us directly at staff@tricountyninja.com.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">6</span>
              Data Security
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                We implement reasonable administrative, technical, and physical security measures to protect your personal information from unauthorized access, use, or disclosure. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">7</span>
              Your Rights
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 mb-4 leading-relaxed">You have the right to:</p>
              <ul className="list-none space-y-2">
                {[
                  'Access and receive a copy of the personal data we hold about you',
                  'Request correction of inaccurate or incomplete data',
                  'Request deletion of your personal data, subject to legal obligations',
                  'Opt out of marketing communications at any time',
                  'Lodge a complaint with a supervisory authority',
                ].map((item) => (
                  <li key={item} className="flex items-start text-gray-600">
                    <i className="ri-checkbox-circle-fill text-[#FF6B35] mr-3 mt-0.5 shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center">
              <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 shrink-0">8</span>
              Changes to This Policy
            </h2>
            <div className="pl-11">
              <p className="text-gray-600 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page with an updated effective date. We encourage you to review this policy periodically for any changes.
              </p>
            </div>
          </section>

          {/* Contact Box */}
          <div className="bg-[#0A1628] rounded-2xl p-8 text-white">
            <h2 className="text-xl font-bold mb-3">Contact Us</h2>
            <p className="text-white/70 mb-6 text-sm leading-relaxed">
              If you have any questions or concerns about this Privacy Policy, please contact us:
            </p>
            <div className="space-y-3">
              <div className="flex items-center text-sm">
                <i className="ri-map-pin-line text-[#FF6B35] mr-3 text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="text-white/80">14283 Fenton Rd, Fenton, MI 48430</span>
              </div>
              <div className="flex items-center text-sm">
                <i className="ri-phone-line text-[#FF6B35] mr-3 text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="text-white/80">(810) 354-8352</span>
              </div>
              <div className="flex items-center text-sm">
                <i className="ri-mail-line text-[#FF6B35] mr-3 text-lg w-5 h-5 flex items-center justify-center"></i>
                <a href="mailto:staff@tricountyninja.com" className="text-white/80 hover:text-[#FF6B35] transition-colors">staff@tricountyninja.com</a>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0A1628] text-white mt-0">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">© 2025 Tri County Ninja. All rights reserved.</p>
            <div className="flex space-x-6 text-sm flex-wrap justify-center gap-y-2">
              <Link to="/privacy-policy" className="text-[#FF6B35] transition-colors cursor-pointer whitespace-nowrap">Privacy Policy</Link>
              <span className="text-gray-600">|</span>
              <Link to="/terms-of-service" className="text-gray-400 hover:text-[#FF6B35] transition-colors cursor-pointer whitespace-nowrap">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
